import { useEffect, useRef } from 'react';

import type { CropRate } from 'hooks/useChemical/types';

import useData from './hooks/useData';

import MixRateRow from './components/MixRateRow';
import { SEARCH_DEBOUNCE_MS, TEST_ID } from './constants';
import styles from './protoStyles.module.scss';
import type { FunctionComponent, Props } from './types';

const formatCropRate = (rate: CropRate): string => {
    if (rate.maxRate == null) {
        return 'No label rate';
    }

    const unit = `${rate.rateUnit}/${rate.ratePerUnit === 'acre' ? 'ac' : rate.ratePerUnit}`;

    if (rate.minRate != null && rate.minRate !== rate.maxRate) {
        return `${rate.minRate}–${rate.maxRate} ${unit}`;
    }

    return `up to ${rate.maxRate} ${unit}`;
};

const formatPhi = (rate: CropRate): string | null => {
    if (rate.phi == null) {
        return null;
    }

    return `${rate.phi} ${rate.phiUnit ?? 'days'} PHI`;
};

const formatRei = (rate: CropRate): string | null => {
    if (rate.rei == null) {
        return null;
    }

    return `${rate.rei} ${rate.reiUnit ?? 'hours'} REI`;
};

const CATEGORY_BADGE_CLASS: Record<string, string> = {
    adjuvant: styles.badgeAdjuvant,
    fungicide: styles.badgeFungicide,
    growth: styles.badgeGrowth,
    herbicide: styles.badgeHerbicide,
    insecticide: styles.badgeInsecticide,
    surfactant: styles.badgeSurfactant
};

// Static formulation-code → description map. Interim workaround because
// ChemicalSearchResult does not yet carry formulationName; Backend handoff
// filed to extend the search response. Drop this map once that ships.
const FORMULATION_CODE_NAMES: Record<string, string> = {
    AS: 'Aqueous Suspension',
    CS: 'Capsule Suspension',
    DF: 'Dry Flowable',
    EC: 'Emulsifiable Concentrate',
    EW: 'Emulsion in Water',
    FL: 'Flowable',
    G: 'Granular',
    ME: 'Microemulsion',
    SC: 'Suspension Concentrate',
    SL: 'Soluble Liquid',
    WDG: 'Water Dispersible Granule',
    WG: 'Wettable Granule',
    WP: 'Wettable Powder'
};

const formatFormulation = (code: string | null, name?: string | null): string | null => {
    if (name && code) {
        return `${name} (${code})`;
    }

    if (name) {
        return name;
    }

    if (!code) {
        return null;
    }

    const mapped = FORMULATION_CODE_NAMES[code.toUpperCase()];

    return mapped ? `${mapped} (${code})` : code;
};

const SIGNAL_CHIP_CLASS: Record<string, string> = {
    caution: styles.signalCaution,
    danger: styles.signalDanger,
    warning: styles.signalWarning
};

const capitalize = (value: string): string =>
    value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

const inferCategoryFromName = (labelName: string): string | null => {
    const lower = labelName.toLowerCase();

    if (lower.includes('herbicide')) {
        return 'Herbicide';
    }

    if (lower.includes('fungicide')) {
        return 'Fungicide';
    }

    if (lower.includes('insecticide')) {
        return 'Insecticide';
    }

    if (lower.includes('adjuvant')) {
        return 'Adjuvant';
    }

    if (lower.includes('surfactant')) {
        return 'Surfactant';
    }

    return null;
};

const CreateMixPage: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const {
        chemicals,
        crop,
        enrichmentById,
        handleAddChemical,
        handleCancel,
        handleNext,
        handleRateChange,
        handleRatePerUnitChange,
        handleRateUnitChange,
        handleRemove,
        handleSave,
        handleSearch,
        isEditing,
        isSearchLoading,
        mixName,
        pendingIds,
        results,
        searchData,
        searchQuery,
        setCrop,
        setMixName,
        setSearchQuery,
        suggestedCrops,
        totalCount
    } = useData();

    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasMore = results.length < totalCount;

    useEffect(() => {
        const node = sentinelRef.current;

        if (!node || !hasMore || isSearchLoading) {
            return;
        }

        const observer = new IntersectionObserver(
            entries => {
                if (entries[0]?.isIntersecting) {
                    handleNext();
                }
            },
            { root: node.parentElement, rootMargin: '40px' }
        );

        observer.observe(node);

        return () => {
            observer.disconnect();
        };
    }, [handleNext, hasMore, isSearchLoading, results.length]);

    useEffect(
        () => () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        },
        []
    );

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);

        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        if (value.trim().length < 2) {
            return;
        }

        searchDebounceRef.current = setTimeout(() => {
            handleSearch(value);
        }, SEARCH_DEBOUNCE_MS);
    };

    const productCount = chemicals.length;
    const inRecipeIds = new Set(chemicals.map(c => c.chemicalId));

    return (
        <div className={styles.createMixPage} data-testid={testId}>
            <div className={styles.pageHeader}>
                <div className={styles.pageHeaderStack}>
                    <h1 className={styles.pageTitle}>{isEditing ? 'Edit Mix' : 'New Mix'}</h1>

                    <div className={styles.pageSubtitle}>
                        Build a reusable recipe. Tank calculations, weather, and compliance are
                        applied separately via Mix &amp; Apply inside an application.
                    </div>
                </div>

                <div className={styles.pageHeaderActions}>
                    <button
                        className={`${styles.btn} ${styles.btnGhost}`}
                        onClick={handleCancel}
                        type='button'>
                        Cancel
                    </button>

                    <button
                        className={`${styles.btn} ${styles.btnPrimary}`}
                        disabled={!mixName.trim() || productCount === 0 || pendingIds.size > 0}
                        onClick={handleSave}
                        type='button'>
                        {isEditing ? 'Save' : 'Save Mix'}
                    </button>
                </div>
            </div>

            <div className={styles.twoPanel}>
                <section className={styles.panel}>
                    <div className={styles.panelHeader}>
                        <div className={styles.panelHeaderTitle}>Product Mix</div>

                        <span className={styles.panelHeaderMeta}>
                            {`${productCount} ${productCount === 1 ? 'product' : 'products'}`}
                        </span>
                    </div>

                    <div className={styles.panelBody}>
                        <div className={styles.panelSection}>
                            <div className={styles.searchWrap}>
                                <svg
                                    className={styles.searchWrapIcon}
                                    fill='none'
                                    height='16'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    viewBox='0 0 24 24'
                                    width='16'>
                                    <circle cx='11' cy='11' r='7' />

                                    <path d='m20 20-3.5-3.5' />
                                </svg>

                                <input
                                    className={styles.input}
                                    onChange={e => handleSearchChange(e.target.value)}
                                    placeholder='Search by product name or EPA number…'
                                    value={searchQuery}
                                />

                                {isSearchLoading && (
                                    <span aria-hidden='true' className={styles.searchWrapSpinner} />
                                )}
                            </div>

                            {results.length > 0 && (
                                <div className={styles.searchResults}>
                                    {results.map(result => {
                                        const resultId =
                                            result.id ?? result.agrianProductId ?? result.labelName;
                                        const isInRecipe = inRecipeIds.has(resultId);
                                        const primaryClassification = result.classifications.find(
                                            classification => classification.primary
                                        );
                                        const categoryName =
                                            primaryClassification?.name ??
                                            inferCategoryFromName(result.labelName);
                                        const categoryKey = categoryName?.toLowerCase() ?? '';
                                        const categoryBadgeClass =
                                            CATEGORY_BADGE_CLASS[categoryKey] ??
                                            styles.badgeDefault;
                                        const subParts = [
                                            result.epaNumber ? `EPA ${result.epaNumber}` : null,
                                            formatFormulation(result.formulationCode),
                                            result.manufacturer
                                        ].filter(Boolean);

                                        return (
                                            <div className={styles.searchResult} key={resultId}>
                                                <div>
                                                    <div className={styles.srTitle}>
                                                        {result.labelName}
                                                    </div>

                                                    <div className={styles.srSub}>
                                                        {subParts.join(' · ')}
                                                    </div>
                                                </div>

                                                <div className={styles.srBadges}>
                                                    {categoryName && (
                                                        <span
                                                            className={`${styles.badge} ${categoryBadgeClass}`}>
                                                            {categoryName}
                                                        </span>
                                                    )}

                                                    <span
                                                        className={`${styles.badge} ${styles.badgeDefault}`}>
                                                        {result.source}
                                                    </span>
                                                </div>

                                                <button
                                                    onClick={() => {
                                                        if (!isInRecipe) {
                                                            handleAddChemical(result);
                                                        }
                                                    }}
                                                    className={`${styles.srAdd}${isInRecipe ? ` ${styles.srAddDisabled}` : ''}`}
                                                    disabled={isInRecipe}
                                                    type='button'>
                                                    {isInRecipe ? '✓' : '+'}
                                                </button>
                                            </div>
                                        );
                                    })}

                                    {hasMore && (
                                        <div className={styles.searchSentinel} ref={sentinelRef} />
                                    )}
                                </div>
                            )}

                            <div className={styles.smallerMuted}>
                                {searchQuery.trim().length < 2
                                    ? 'Type at least 2 characters to search.'
                                    : searchData === undefined || isSearchLoading
                                      ? 'Searching…'
                                      : results.length === 0
                                        ? 'No results.'
                                        : hasMore
                                          ? `Showing ${results.length} of ${totalCount}${isSearchLoading ? ' · loading more…' : ''}`
                                          : `${results.length} result${results.length === 1 ? '' : 's'}`}
                            </div>
                        </div>

                        <div className={styles.panelSection}>
                            <div className={styles.panelSectionTitleRow}>
                                <span className={styles.panelSectionTitle}>
                                    Recipe · {productCount} Products
                                </span>

                                <span className={styles.smallerMuted}>
                                    Auto-sorted by label mix order
                                </span>
                            </div>

                            {productCount === 0 ? (
                                <div
                                    className={`${styles.banner} ${styles.bannerInfo} ${styles.bannerSmaller}`}>
                                    No products yet. Search above to add your first product.
                                </div>
                            ) : (
                                chemicals.map((chemical, index) => {
                                    const isPending = pendingIds.has(chemical.chemicalId);
                                    const enrichment = enrichmentById[chemical.chemicalId];
                                    const chemicalCategory =
                                        chemical.classification ??
                                        inferCategoryFromName(chemical.labelName);
                                    const classificationKey = chemicalCategory?.toLowerCase() ?? '';
                                    const categoryBadgeClass =
                                        CATEGORY_BADGE_CLASS[classificationKey] ??
                                        styles.badgeDefault;
                                    const signalKey = enrichment?.signalWord?.toLowerCase() ?? '';
                                    const signalChipClass = SIGNAL_CHIP_CLASS[signalKey];
                                    const formulationDisplay = formatFormulation(
                                        chemical.formulationCode,
                                        enrichment?.formulationName
                                    );
                                    const subParts = [
                                        chemical.epaNumber ? `EPA ${chemical.epaNumber}` : null,
                                        formulationDisplay,
                                        enrichment?.manufacturer
                                    ].filter(Boolean);
                                    const cropMatch = crop
                                        ? enrichment?.cropRates.find(rate => rate.crop === crop)
                                        : undefined;
                                    const hasLabelRateRange =
                                        enrichment?.genericMinRate !== undefined &&
                                        enrichment?.genericMinRate !== null &&
                                        enrichment?.genericMaxRate !== undefined &&
                                        enrichment?.genericMaxRate !== null;
                                    const cropPhi = cropMatch ? formatPhi(cropMatch) : null;
                                    const cropRei = cropMatch ? formatRei(cropMatch) : null;

                                    return (
                                        <div
                                            className={styles.productCard}
                                            key={chemical.chemicalId}>
                                            <div className={styles.pcHeader}>
                                                <div className={styles.pcTitleRow}>
                                                    <div className={styles.orderBadge}>
                                                        {index + 1}
                                                    </div>

                                                    <div className={styles.pcTitleCol}>
                                                        <div className={styles.pcTitle}>
                                                            {chemical.labelName}
                                                        </div>

                                                        {subParts.length > 0 && (
                                                            <div className={styles.pcSub}>
                                                                {subParts.join(' · ')}
                                                            </div>
                                                        )}

                                                        {(chemicalCategory ||
                                                            chemical.formulationCode ||
                                                            signalChipClass) && (
                                                            <div className={styles.pcBadges}>
                                                                {chemicalCategory && (
                                                                    <span
                                                                        className={`${styles.badge} ${categoryBadgeClass}`}>
                                                                        {chemicalCategory}
                                                                    </span>
                                                                )}

                                                                {chemical.formulationCode && (
                                                                    <span
                                                                        className={`${styles.badge} ${styles.badgeDefault}`}>
                                                                        {chemical.formulationCode}
                                                                    </span>
                                                                )}

                                                                {signalChipClass &&
                                                                    enrichment?.signalWord && (
                                                                        <span
                                                                            className={`${styles.signalChip} ${signalChipClass}`}>
                                                                            {capitalize(
                                                                                enrichment.signalWord
                                                                            )}
                                                                        </span>
                                                                    )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() =>
                                                        handleRemove({
                                                            chemicalId: chemical.chemicalId
                                                        })
                                                    }
                                                    className={styles.pcRemove}
                                                    type='button'>
                                                    ×
                                                </button>
                                            </div>

                                            {isPending ? (
                                                <div className={styles.pcPending}>
                                                    <span
                                                        aria-hidden='true'
                                                        className={styles.pcPendingShimmer}
                                                    />

                                                    <span className={styles.pcPendingText}>
                                                        Gathering label data…
                                                    </span>
                                                </div>
                                            ) : (
                                                <MixRateRow
                                                    rangeHint={
                                                        cropMatch
                                                            ? `${crop}: ${formatCropRate(cropMatch)}${cropPhi ? ` · ${cropPhi}` : ''}${cropRei ? ` · ${cropRei}` : ''}`
                                                            : hasLabelRateRange
                                                              ? `Label: ${enrichment?.genericMinRate}–${enrichment?.genericMaxRate} ${chemical.rateUnit}/${chemical.ratePerUnit === 'acre' ? 'ac' : chemical.ratePerUnit}`
                                                              : null
                                                    }
                                                    chemicalId={chemical.chemicalId}
                                                    onRateChange={handleRateChange}
                                                    onRatePerUnitChange={handleRatePerUnitChange}
                                                    onRateUnitChange={handleRateUnitChange}
                                                    rate={chemical.applicationRate}
                                                    ratePerUnit={chemical.ratePerUnit}
                                                    rateUnit={chemical.rateUnit}
                                                />
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </section>

                <section className={styles.panel}>
                    <div className={styles.panelHeader}>
                        <div className={styles.panelHeaderTitle}>Mix Details</div>
                    </div>

                    <div className={styles.panelBody}>
                        <div className={styles.field}>
                            <label className={styles.fieldLabel}>Mix Name</label>

                            <input
                                className={`${styles.input} ${styles.inputBig}`}
                                onChange={e => setMixName(e.target.value)}
                                placeholder='e.g., Corn Preemerge Standard'
                                value={mixName}
                            />
                        </div>

                        <div className={styles.panelSection}>
                            <div className={styles.panelSectionTitle}>About</div>

                            <div className={styles.smallerMuted}>
                                A mix captures{' '}

                                <strong>which products go together at what rates</strong>. It does
                                not include tank size, field acres, weather, or state compliance —
                                those come from the application context when the mix is used in Mix
                                &amp; Apply.
                            </div>
                        </div>

                        <div className={styles.panelSection}>
                            <div className={styles.panelSectionTitle}>
                                Suggested Crop (optional)
                            </div>

                            <select
                                className={`${styles.selectInput} ${styles.inputSm}`}
                                disabled={suggestedCrops.length === 0}
                                onChange={e => setCrop(e.target.value)}
                                value={crop}>
                                <option value=''>
                                    {suggestedCrops.length === 0
                                        ? '— add products to see crops —'
                                        : '— none —'}
                                </option>

                                {suggestedCrops.map(name => (
                                    <option key={name} value={name}>
                                        {name}
                                    </option>
                                ))}
                            </select>

                            <div className={styles.smallerMuted}>
                                Used to pre-select the crop rate range in the Recipe builder, and to
                                suggest this mix when planning an application for matching crops.
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default CreateMixPage;
